import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import Video from '@cartwhl/ui/atoms/Video';

import { posterForVideoSource } from '../../marketing-media';

interface BlogVideoProps {
  src: string;
  description: string;
  className?: string;
}

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';

interface SaveDataConnection {
  saveData?: boolean;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

export default function BlogVideo({
  src,
  description,
  className = '',
}: BlogVideoProps) {
  // null keeps media sources out of SSR and the first client render.
  const [stillPreferred, setStillPreferred] = useState<boolean | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playBlocked, setPlayBlocked] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const query = window.matchMedia(reducedMotionQuery);
    const connection = (
      navigator as Navigator & { connection?: SaveDataConnection }
    ).connection;
    const update = () => {
      setStillPreferred(query.matches || connection?.saveData === true);
      setPlaying(false);
      setPlayBlocked(false);
    };
    update();
    query.addEventListener('change', update);
    connection?.addEventListener?.('change', update);
    return () => {
      query.removeEventListener('change', update);
      connection?.removeEventListener?.('change', update);
    };
  }, []);

  const togglePlayback = () => {
    if (playing) {
      videoRef.current?.pause();
      setPlaying(false);
      return;
    }

    // Mount within the click gesture so browsers can grant manual playback.
    flushSync(() => setPlaying(true));
    void videoRef.current?.play().catch(() => {
      setPlaying(false);
      setPlayBlocked(true);
    });
  };

  return (
    <div className={`bg-tertiary relative ${className}`}>
      <img
        src={posterForVideoSource(src)}
        alt={description}
        loading="lazy"
        decoding="async"
        className="block h-auto w-full"
      />
      {stillPreferred === false && !playBlocked && (
        <Video
          src={src}
          mode="autoplay"
          strictViewportPlayback
          poster={posterForVideoSource(src)}
          className="absolute inset-0 h-full w-full rounded-[inherit]"
          onPlayRejected={() => setPlayBlocked(true)}
        />
      )}
      {playing && (
        <video
          ref={videoRef}
          src={src}
          poster={posterForVideoSource(src)}
          preload="none"
          muted
          playsInline
          onEnded={() => setPlaying(false)}
          className="absolute inset-0 h-full w-full rounded-[inherit] object-cover"
          aria-label={description}
        />
      )}
      {(stillPreferred === true || playBlocked) && (
        <button
          type="button"
          onClick={togglePlayback}
          aria-label={`${playing ? 'Stop' : 'Play'} ${description}`}
          className="absolute inset-0 z-10 flex items-center justify-center text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-green-50"
        >
          {!playing && (
            <svg
              aria-hidden="true"
              viewBox="0 0 20 20"
              className="h-12 w-12 fill-current drop-shadow-lg"
            >
              <path d="M5 3.5a1 1 0 0 1 1.5-.86l10 6.5a1 1 0 0 1 0 1.72l-10 6.5A1 1 0 0 1 5 16.5z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}
